import { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import ExportedImage from 'next-image-export-optimizer';
import Link from 'next/link';

import { Combobox } from '@headlessui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useScroll, useTransform } from 'framer-motion';
import kwesforms from 'kwesforms';

import AnimateWhileInView from '@/components/shared/AnimateWhileInView';
import BgEllipse from '@/components/shared/lp/BgEllipse';

import { siteData } from '@/data/configs/site';
import komodoAmbassador from '@/public/images/lp/community/ambassador.png';
import komodoCommunityCurrency from '@/public/images/lp/community/communityCurrency.png';
import discordCard from '@/public/images/lp/community/discord-card.png';
import komodoHandbook from '@/public/images/lp/community/handbook.png';
import komodoWorld from '@/public/images/lp/community/komodo-world.png';
import logoStairs from '@/public/images/lp/community/logo_stairs.png';
import redditCard from '@/public/images/lp/community/reddit-card.png';
import telegramCard from '@/public/images/lp/community/telegram-card.png';
import twitterCard from '@/public/images/lp/community/x-card.png';
import gradientLinkArrow from '@/public/images/lp/svgs/gradient-link-arrow.svg';
import link from '@/public/images/lp/svgs/link.svg';
import checkbox from '@/public/img/community/applicationForm/checkbox.svg';
import discordIcon from '@/public/img/community/applicationForm/discord.svg';
import dropdown from '@/public/img/community/applicationForm/dropdown.svg';
import informationIcon from '@/public/img/community/applicationForm/info.svg';
import infoBg from '@/public/img/community/applicationForm/infoBg.svg';
import telegramIcon from '@/public/img/community/applicationForm/telegram.svg';
import discord1 from '@/public/img/community/socials/discord.svg';
import linkedin from '@/public/img/community/socials/linkedin.svg';
import messenger from '@/public/img/community/socials/messenger.svg';
import redditIcon from '@/public/img/community/socials/reddit.svg';
import telegram1 from '@/public/img/community/socials/telegram.svg';
import twitter1 from '@/public/img/community/socials/twitter.svg';
import twitterIcon from '@/public/img/community/socials/twitterIcon.svg';
import youtube from '@/public/img/community/socials/youtube.svg';

// const ref = useRef(null);

// useEffect(() => {
//   if (ref.current) {
//     const { scrollYProgress } = useScroll({
//       target: ref,
//       offset: ["start center", "end center"],
//     });
//     const opacity = useTransform(
//       scrollYProgress,
//       [0, 0.5, 1],
//       [0, 1, 0],
//     );

//     // You might want to use the opacity value here, for example:
//     // ref.current.style.opacity = opacity;
//   }
// }, [ref]);

const socialIcons = [
  {
    name: 'Discord',
    icon: discord1,
    link: siteData.social.discord,
  },
  {
    name: 'Telegram',
    icon: telegram1,
    link: siteData.social.telegram,
  },
  {
    name: 'X.com',
    icon: twitter1,
    link: siteData.social.twitter,
  },
  {
    name: 'Linkedin',
    icon: linkedin,
    link: siteData.social.linkedin,
  },
  {
    name: 'Youtube',
    icon: youtube,
    link: siteData.social.youtube,
  },
];

export default function Community() {
  // Add state for countries, cities, and selections
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [countryQuery, setCountryQuery] = useState('');
  const [cityQuery, setCityQuery] = useState('');

  // Create refs for scroll animations
  const headerRef = useRef(null);

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.04, 0.62, 0.23, 0.98],
      },
    },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const floatAnimation = {
    initial: { y: 0 },
    animate: {
      y: [-8, 8, -8],
      transition: {
        duration: 4,
        repeat: Infinity,
        repeatType: 'reverse',
        ease: 'easeInOut',
      },
    },
  };

  // Animation variants for gradient ellipses
  const ellipseAnimation = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 1.2,
        ease: 'easeOut',
      },
    },
  };

  // Animation variants for community gradient
  const gradientAnimation = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 1.6,
        ease: 'easeOut',
      },
    },
  };

  // Fetch countries on component mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await fetch('/countries-cities/countries.json');
        const data = await response.json();
        setCountries(data);
      } catch (error) {
        console.error('Error fetching countries:', error);
      }
    };
    fetchCountries();
  }, []);

  // Fetch cities when country is selected
  useEffect(() => {
    const fetchCities = async () => {
      if (selectedCountry) {
        try {
          //console.log(`Fetching cities for ${selectedCountry}`);
          const response = await fetch(
            `/countries-cities/cities/${selectedCountry.toLowerCase()}.json`,
          );
          if (!response.ok) {
            // console.log(`No cities found for ${selectedCountry}`);
            setCities([]);
            return;
          }
          const data = await response.json();
          //console.log(`Successfully loaded ${data.length} cities for ${selectedCountry}`);
          setCities(data);
        } catch (error) {
          console.error(`Error fetching cities for ${selectedCountry}:`, error);
          setCities([]);
        }
      } else {
        setCities([]);
      }
    };
    fetchCities();
  }, [selectedCountry]);

  // Initialize kwesforms
  useEffect(() => {
    kwesforms.init();
  }, []);

  // Handle country selection
  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setSelectedCity('');
    setShowCountryDropdown(false);
  };

  // Handle city selection
  const handleCitySelect = (city) => {
    setSelectedCity(city);
    setShowCityDropdown(false);
  };

  const filteredCountries =
    countryQuery === ''
      ? countries
      : countries.filter((country) => country.toLowerCase().includes(countryQuery.toLowerCase()));

  const filteredCities =
    cityQuery === ''
      ? cities
      : cities.filter((city) => city.toLowerCase().includes(cityQuery.toLowerCase()));

  return (
    <>
      <div suppressHydrationWarning className='absolute z-[-11] h-full w-full overflow-hidden'>
        <motion.div
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true, amount: 0.3 }}
          variants={gradientAnimation}
        >
          <ExportedImage
            placeholder='empty'
            alt='Community Gradient'
            src='/svg/gradients/community/community-gradient.svg'
            className='absolute left-0 top-[900px] h-auto w-[calc(1920px*0.65)] md:top-[800px] lg:top-[600px] lg:w-[calc(1920px*0.65)] xl:top-[600px] xl:w-[calc(1920px*0.75)] 2xl:top-[600px] 2xl:w-[calc(1920px*0.75)] 3xl:top-[600px] 3xl:w-[calc(1920px*0.85)]'
            height={1535}
            width={1581}
          />
        </motion.div>
      </div>
      <main suppressHydrationWarning className='relative z-[100] mx-auto font-sans text-white'>
        <motion.div
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true }}
          variants={ellipseAnimation}
          className='lp-ellipse-223 absolute left-[30%] top-[-200px] z-[-10] h-[359px] w-[287px] -translate-x-1/2 lg:left-[40%] lg:hidden -sm:top-[-200px]'
        ></motion.div>

        <motion.div
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true }}
          variants={ellipseAnimation}
          className='absolute bottom-[600px] left-[-100px] z-[-10] lg:h-[739px] lg:w-[755px] 3xl:-left-[300px] 3xl:bottom-[600px] 3xl:h-[1000px] 3xl:w-[1000px]'
          style={{
            borderRadius: '755px',
            opacity: 0.65,
            background: 'rgba(140, 140, 255, 0.59)',
            mixBlendMode: 'plus-lighter',
            filter: 'blur(139.85000610351562px)',
          }}
        ></motion.div>

        <motion.div
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true }}
          variants={ellipseAnimation}
          className='absolute bottom-[-200px] right-[-100px] z-[-10] lg:h-[508px] lg:w-[519px] 3xl:h-[910px] 3xl:w-[875px]'
          style={{
            borderRadius: '519px',
            opacity: 0.65,
            background: 'rgba(140, 140, 255, 0.59)',
            mixBlendMode: 'plus-lighter',
            filter: 'blur(139.85000610351562px)',
          }}
        ></motion.div>

        <div className='dummy hidden w-[50.21px] w-[70.21px] lg:w-[125.21px] lg:w-[90.21px]'></div>
        <div
          suppressHydrationWarning
          className='container mx-auto items-center justify-center md:grid md:grid-cols-24 md:gap-0'
        >
          <div className='hidden xl:col-span-2 xl:block'></div>
          <div className='relative mx-auto w-full max-w-7xl items-center justify-center px-4 xs:px-8 md:col-span-24 md:mx-auto xl:col-span-20 xl:px-0'>
            <motion.section
              ref={headerRef}
              initial='hidden'
              whileInView='visible'
              viewport={{ once: true, amount: 0.1 }}
              variants={staggerContainer}
              className='flex flex-col items-center justify-center bg-[url(/svg/gradients/community/mesh.svg)] pt-24 md:pt-36 lg:flex-row lg:pt-36'
            >
              <div className='relative z-10 mx-auto w-full bg-black bg-opacity-10 text-center sm:max-w-[600px] lg:max-w-[800px]'>
                <motion.h1
                  variants={fadeInUp}
                  className='flex flex-col font-heading text-4xl font-semibold leading-tight tracking-[-2.88px] md:text-5xl md:leading-[3.5rem] md:tracking-[-2px] lg:text-6xl lg:leading-[4rem] lg:tracking-[-2.5px] xl:text-[80px] xl:leading-[110%] xl:tracking-[-3px] 2xl:text-[96px]'
                >
                  <span className='community-text font-semibold text-transparent'>
                    Community-first
                  </span>{' '}
                  <span className='font-normal'>Approach</span>
                </motion.h1>
                <motion.div
                  variants={fadeInUp}
                  className='event-centric-btn py-[15px] text-center font-sans text-xl font-normal md:py-[18px] md:text-2xl lg:py-[20px] lg:text-[28px] 2xl:text-[32px] 2xl:leading-[120%]'
                >
                  Collaborative Network
                </motion.div>
                <motion.div
                  variants={fadeInUp}
                  className='mx-auto mt-4 w-full text-center text-lg leading-[1.6] sm:w-[550px] md:text-xl md:leading-[1.7] lg:w-[625px] lg:text-[22px] lg:leading-[1.5]'
                >
                  Komodo is a community of hackers, developers, entrepreneurs, and enthusiasts
                  pushing the boundaries of blockchain technology.
                </motion.div>
              </div>
            </motion.section>

            <motion.section
              initial='hidden'
              whileInView='visible'
              viewport={{ once: true, amount: 0.2 }}
              variants={staggerContainer}
              className='relative z-10 mx-auto mt-32 grid items-center gap-20 pb-[110px] lg:grid-cols-2'
            >
              <motion.div variants={fadeInUp} className=''>
                <div className='-lg:text-center'>
                  <h2 className='font-heading text-3xl font-semibold leading-tight tracking-[1px] md:text-3xl md:leading-[1.2] md:tracking-[1.5px] lg:text-4xl lg:leading-[1.25] lg:tracking-[2px] xl:text-[48px] xl:leading-[58px]'>
                    Why Komodo Crypto Community?
                  </h2>
                  <p className='pt-[15px] font-sans text-base leading-[1.5] text-br-semi-white md:text-[17px] lg:text-[18px] lg:leading-[1.6] 2xl:w-[634px]'>
                    Komodo&apos;s blockchain ecosystem is built by and for passionate technology
                    pioneers.
                  </p>
                  <p className='pb-6 pt-[11px] font-sans text-base leading-[1.5] text-br-semi-white md:text-[17px] lg:text-[18px] lg:leading-[1.6] 2xl:w-[634px]'>
                    Become part of our dynamic crypto community where innovative minds collaborate
                    to advance digital freedom and self-sovereignty.
                  </p>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Link
                      href='https://komodoplatform.com/discord'
                      target='_blank'
                      className='join-now-btn px-4 py-[11px] text-base font-medium text-white md:text-lg'
                    >
                      Join the Movement
                    </Link>
                  </motion.div>
                </div>
              </motion.div>
              <motion.div variants={floatAnimation} initial='initial' animate='animate'>
                <ExportedImage
                  placeholder='empty'
                  alt='komodoWorld'
                  className='mx-auto h-auto w-[560px]'
                  src={komodoWorld}
                />
              </motion.div>
            </motion.section>
          </div>
          <div className='hidden xl:col-span-2 xl:block'></div>
        </div>
        <section className='relative flex flex-col items-center justify-center py-[30px] pb-[20px] lg:flex-row lg:gap-[10px] lg:pb-0 xl:gap-[60px]'>
          <motion.div
            initial={{ opacity: 0, x: -180, y: 0 }}
            whileInView={{ opacity: 1, x: 0, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{
              duration: 0.9,
              type: 'spring',
              stiffness: 90,
              damping: 20,
            }}
            className='flex h-[220px] w-[90%] flex-col justify-center rounded-[17.817px] backdrop-blur-[20.14px] xms:w-[80%] sm:w-[550px] lg:w-[500px] lg:pr-[65px] xl:w-[600px] 2xlm:h-[250px] 2xlm:w-[787px] 2xlm:py-[50px] 2xlm:pr-[89px] -lg:bg-opacity-10 -sm:px-2'
            style={{
              background:
                'linear-gradient(35deg, rgb(83, 109, 203) 0%, rgb(0, 0, 0) 50%) left bottom',
              backgroundPosition: 'bottom left',
              borderRadius: '17.817px',
              border: '0.848px solid #493db5',
            }}
          >
            <h2 className='text-center font-heading text-xl font-semibold leading-tight text-white md:text-[24px] lg:text-right lg:text-[28px] 2xl:text-[40px]'>
              Join the Best <br />
              Blockchain Community
            </h2>
            <p className='pt-[15px] text-center text-sm leading-[1.5] text-white sm:text-base sm:leading-[1.6] md:pt-[18px] lg:pt-[22px] lg:text-right lg:text-base lg:leading-[1.6]'>
              Be a Komodian. Contribute, chat and trade among Komodians.
              <br />
              Follow our socials to stay updated!
            </p>
          </motion.div>
          <div className='pt-10 lg:pt-0'>
            <motion.div
              initial={{ opacity: 0, x: 180, y: 0 }}
              whileInView={{ opacity: 1, x: 0, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{
                duration: 0.9,
                type: 'spring',
                stiffness: 90,
                damping: 20,
              }}
              className='socials-bg relative mx-auto flex h-[150px] w-[90%] shrink-0 flex-row items-center justify-between p-8 py-[40px] xms:w-[80%] sm:h-[220px] sm:w-[550px] lg:w-[500px] xl:w-[600px] 2xlm:h-[250px] 2xlm:w-[787px] 2xlm:px-[25px] 2xlm:py-[25px]'
            >
              {socialIcons.map((socialIcon, index) => (
                <motion.div
                  key={`social-icon-${index}`}
                  whileHover={{ scale: 1.15, rotate: [0, -5, 5, 0], transition: { duration: 0.5 } }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Link href={socialIcon.link} target='_blank'>
                    <ExportedImage
                      src={socialIcon.icon}
                      alt={`social-icon-${index}`}
                      className='h-[50px] w-[50px] sm:h-[70px] sm:w-[70px] md:h-[90px] md:w-[90px]'
                    />
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
        <div
          suppressHydrationWarning
          className='container z-10 mx-auto items-center justify-center md:grid md:grid-cols-24 md:gap-0'
        >
          <div className='hidden xl:col-span-2 xl:block'></div>
          <div className='relative w-full max-w-7xl px-4 py-[47px] xs:px-8 md:col-span-24 md:mx-auto xl:col-span-20 xl:px-0'>
            <motion.section
              initial='hidden'
              whileInView='visible'
              viewport={{ once: true, amount: 0.1 }}
              variants={staggerContainer}
              className='relative z-10 mx-auto h-auto w-full items-center justify-center py-6'
            >
              <ul className='mx-auto grid items-center justify-center gap-x-[20px] gap-y-[20px] overflow-hidden lg:grid-cols-2 lg:gap-y-[0px] xl:gap-x-[50px] 2xl:gap-x-[79px]'>
                <motion.li
                  variants={fadeInUp}
                  whileHover={{
                    y: -10,
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
                    transition: { duration: 0.3 },
                  }}
                  key={'komodo-community-currency'}
                  className='community-cards-bg relative mx-auto flex w-[320px] flex-col items-center justify-center xs:w-[400px] sm:h-[550px] sm:w-[550px] lg:w-[450px] xl:h-[591px] xl:w-[540px]'
                  style={{
                    border: '0.848px solid #1d4b6c',
                  }}
                >
                  <div
                    className='relative w-full rounded-[17.817px] bg-opacity-50'
                    style={{
                      background:
                        'radial-gradient(53.51% 53.7% at 49.94% 50.16%, #2426B2 0%, #1E1138 100%)',
                    }}
                  >
                    <ExportedImage
                      placeholder='empty'
                      alt='komodoCommunityCurrency'
                      src={komodoCommunityCurrency}
                      className='mx-auto h-auto w-[75%]'
                    />
                  </div>
                  <div className='flex flex-1 flex-col justify-between px-10 pb-[20px] xl:pb-[42px]'>
                    <h2 className='pt-4 font-heading text-lg font-semibold text-white xs:text-3xl md:leading-[42px] lg:text-[30px] xl:text-2xl xl:text-[40px]'>
                      Komodo Community Cryptocurrency
                    </h2>
                    <p className='pt-2 font-sans text-base font-normal leading-[23px] sm:pt-3'>
                      $KMD can be traded on popular cryptocurrency exchanges and used for payments
                      at a variety of physical stores and online retailers.
                    </p>
                    <motion.div whileHover={{ x: 5 }} transition={{ duration: 0.3 }}>
                      <Link
                        href={'/en/wallet/'}
                        target='_blank'
                        className='block w-max font-semibold text-transparent'
                      >
                        <div className='flex flex-row items-center'>
                          <span className='cards-btn font-bold text-transparent'>
                            Browse Wallets
                          </span>
                          <ExportedImage
                            placeholder='empty'
                            src={gradientLinkArrow}
                            className='ml-2 h-[12px] w-auto'
                            width={12}
                            height={12}
                            alt='link'
                          />
                        </div>
                      </Link>
                    </motion.div>
                  </div>
                </motion.li>
                <motion.li
                  variants={fadeInUp}
                  whileHover={{
                    y: -10,
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
                    transition: { duration: 0.3 },
                  }}
                  key={'komodo-handbook'}
                  className='community-cards-bg mx-auto flex w-[320px] flex-col items-center justify-center xs:w-[400px] sm:h-[550px] sm:w-[550px] lg:w-[450px] xl:h-[591px] xl:w-[540px]'
                  style={{
                    border: '0.848px solid #1d4b6c',
                  }}
                >
                  <div
                    className='relative w-full rounded-[17.817px]'
                    style={{
                      background:
                        'radial-gradient(53.51% 53.7% at 49.94% 50.16%, #2426B2 0%, #1E1138 100%)',
                    }}
                  >
                    <ExportedImage
                      placeholder='empty'
                      src={komodoHandbook}
                      alt='komodoHandbook'
                      className='h-auto w-full'
                    />
                  </div>
                  <div className='flex flex-1 flex-col justify-between px-10 pb-[20px] xl:pb-[42px]'>
                    <h2 className='pt-4 font-heading text-lg font-semibold text-white xs:text-3xl md:leading-[42px] lg:text-[30px] xl:text-2xl xl:text-[40px]'>
                      Komodo Handbook
                    </h2>
                    <p className='pt-2 font-sans text-base font-normal leading-[23px] sm:pt-3'>
                      Learn everything you need to know about Komodo&apos;s technology and how you
                      can get involved with the community.
                    </p>
                    <motion.div whileHover={{ x: 5 }} transition={{ duration: 0.3 }}>
                      <Link
                        href={'https://komodoplatform.com/en/blog/komodo-handbook/'}
                        target='_blank'
                        className='block w-max font-semibold text-transparent'
                      >
                        <div className='flex flex-row items-center'>
                          <span className='cards-btn font-bold text-transparent'>Read Now</span>
                          <ExportedImage
                            placeholder='empty'
                            src={gradientLinkArrow}
                            className='ml-2 h-[12px] w-auto'
                            width={12}
                            height={12}
                            alt='link'
                          />
                        </div>
                      </Link>
                    </motion.div>
                  </div>
                </motion.li>
              </ul>
            </motion.section>
          </div>
          <div className='hidden xl:col-span-2 xl:block'></div>
        </div>

        <div
          suppressHydrationWarning
          className='container mx-auto items-center justify-center md:grid md:grid-cols-24 md:gap-0'
        >
          <div className='hidden xl:col-span-2 xl:block'></div>
          <div className='relative w-full max-w-7xl px-4 py-[47px] xs:px-8 md:col-span-24 md:mx-auto xl:col-span-20 xl:px-0'>
            <motion.section
              suppressHydrationWarning
              initial={{ opacity: 0, y: 80 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{
                duration: 0.9,
                type: 'spring',
                stiffness: 50,
                damping: 15,
              }}
              className='community-cards-bg relative z-10 p-8 lg:max-w-[920px] xl:max-w-[1159px] xl:p-12'
              style={{
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.9)',
              }}
            >
              <div className='gap-15 mb-10 flex w-full flex-col items-start justify-between lg:flex-row'>
                <div className='flex max-w-[709px] flex-col gap-4'>
                  <h2 className='font-heading text-2xl font-semibold leading-tight tracking-[-0.8px] text-white md:text-[28px] md:tracking-[-1px] lg:text-3xl lg:tracking-[-1.2px] xl:text-[36px]'>
                    Join the Komodo Ambassador Program
                  </h2>
                  <p className='text-base leading-[1.6] text-br-semi-white md:text-[17px] md:leading-[1.7]'>
                    <span className='font-bold'>
                      {' '}
                      Pre-apply and get informed first about the ambassador program launch.
                    </span>
                    <br />
                    <br />
                    Join the Komodo Ambassador Program and help drive blockchain innovation on a
                    global scale. Whether you&apos;re a thought leader, regional community builder,
                    or platform expert, there&apos;s a path for you.
                    <br />
                    <br />
                    Help as an ambassador, you&apos;ll create impactful content, lead local
                    initiatives, or engage with blockchain communities while gaining exclusive
                    access to resources, rewards, and professional growth opportunities. Together,
                    let&apos;s grow the decentralized future.
                    <br />
                    <br />
                    <motion.span
                      whileHover={{ x: 5, textDecoration: 'underline' }}
                      transition={{ duration: 0.2 }}
                      className='inline-block'
                    >
                      <Link
                        href='TODO update after blogpost'
                        className='font-medium text-[#91a1ff] underline'
                      >
                        Learn more about the program
                        <ExportedImage
                          placeholder='empty'
                          src={link}
                          className='ml-2 inline h-[12px] w-auto'
                          width={12}
                          height={12}
                          alt='link'
                        />
                      </Link>
                    </motion.span>
                  </p>
                </div>

                <ExportedImage
                  placeholder='empty'
                  className='absolute right-[-67px] top-0 z-[-10] hidden h-auto w-[500px] lg:flex xl:right-[-60px] xl:w-[650px] 2xl:w-[800px]'
                  src={komodoAmbassador}
                  alt='Ambassador Program'
                />
              </div>

              {/* Form Content */}
              <div className='w-full'>
                <h3 className='mb-6 text-xl font-semibold leading-tight text-white md:text-2xl'>
                  Application Form
                </h3>
                <form
                  action='https://kwesforms.com/api/f/fGsANXoPSIFnxfQdBR5a'
                  className='kwes-form space-y-5'
                  data-kw-no-reload='true'
                  data-kw-recaptcha-v2='true'
                >
                  {/* Form Grid Layout */}
                  <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
                    {/* Full Name Field */}
                    <fieldset>
                      <div className='flex flex-col gap-[10px]'>
                        <div className='flex flex-col'>
                          <label className='text-sm font-medium leading-tight text-white md:text-[15px]'>
                            Full Name<span className='text-[#d54f49]'>*</span>
                          </label>
                          {/* <span className='text-xs text-[#cccccc]'>Enter your full name.</span> */}
                        </div>
                        <input
                          type='text'
                          name='fullName'
                          placeholder='Full name'
                          rules='required|min:2'
                          className='h-12 w-full rounded-lg border border-[#483cb5] bg-transparent p-3.5 text-sm text-white placeholder:text-[#b3b3b3] focus:border-[#6daad3] focus:outline-none'
                        />
                      </div>
                    </fieldset>
                    {/* Email Field */}
                    <fieldset>
                      <div className='flex flex-col gap-[10px]'>
                        <div className='flex flex-col'>
                          <label className='text-sm font-medium leading-tight text-white md:text-[15px]'>
                            Email Address<span className='text-[#d54f49]'>*</span>
                          </label>
                          {/* <span className='text-xs text-[#cccccc]'>Enter your email address.</span> */}
                        </div>
                        <input
                          type='email'
                          name='emailAddress'
                          rules='required|email'
                          placeholder='Email address'
                          className='h-12 w-full rounded-lg border border-[#483cb5] bg-transparent p-3.5 text-sm text-white placeholder:text-[#b3b3b3] focus:border-[#6daad3] focus:outline-none'
                        />
                      </div>
                    </fieldset>
                    {/* Social Handles */}
                    <div className='col-span-full flex w-full flex-col gap-[24px] lg:w-[1054px] lg:flex-row'>
                      <fieldset rules='required'>
                        <div className='flex flex-col'>
                          <div className=''>
                            <p className='text-sm font-medium text-white'>
                              Telegram/Discord Handle<span className='text-[#d54f49]'>*</span>
                            </p>
                          </div>
                          <div className='flex flex-col gap-5 pt-[10px] sm:flex-row'>
                            <div className='flex flex-1 items-center gap-[10px] lg:w-[200px] xl:w-[220px] xlm:w-[253px]'>
                              <ExportedImage
                                src={telegramIcon}
                                className='h-[45px] w-[45px]'
                                alt='telegramIcon'
                              />
                              <input
                                type='text'
                                name='telegramHandle'
                                rules='required'
                                placeholder='Telegram handle'
                                className='h-12 w-full rounded-lg border border-[#483cb5] bg-transparent p-3.5 text-sm text-white placeholder:text-[#b3b3b3] focus:border-[#6daad3] focus:outline-none'
                              />
                            </div>
                            <div className='flex flex-1 items-center gap-[10px] lg:w-[200px] xl:w-[220px] xlm:w-[253px]'>
                              <ExportedImage
                                src={discordIcon}
                                className='h-[45px] w-[45px]'
                                alt='discordIcon'
                              />
                              <input
                                type='text'
                                name='discordHandle'
                                rules='required'
                                placeholder='Discord handle'
                                className='h-12 w-full rounded-lg border border-[#483cb5] bg-transparent p-3.5 text-sm text-white placeholder:text-[#b3b3b3] focus:border-[#6daad3] focus:outline-none'
                              />
                            </div>
                          </div>
                        </div>
                      </fieldset>

                      {/* country/city Field */}
                      <fieldset>
                        <div className='flex flex-col'>
                          <div className=''>
                            <p className='text-sm font-medium text-white'>
                              Region/Location<span className='text-[#d54f49]'>*</span>
                            </p>
                          </div>
                          <div className='flex w-full flex-col gap-5 pt-[10px] sm:flex-row'>
                            <div className='flex flex-1 items-center gap-1.5 lg:w-[200px] xl:w-[220px] xlm:w-[253px]'>
                              <div className='relative w-full'>
                                {/* Hidden input to store the actual country value for form submission */}
                                <input
                                  type='hidden'
                                  name='country'
                                  value={selectedCountry}
                                  rules='required'
                                />
                                <div
                                  onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                                  className='cursor-pointer'
                                >
                                  <div className='inline-flex h-12 w-full items-center justify-between rounded-lg border border-[#483cb5] bg-transparent p-3.5 text-sm text-[#b3b3b3] focus:border-[#6daad3]'>
                                    <span>{selectedCountry || 'Country'}</span>
                                    <svg
                                      xmlns='http://www.w3.org/2000/svg'
                                      width='25'
                                      height='24'
                                      viewBox='0 0 25 24'
                                      fill='none'
                                      className={`ml-2 transform transition-transform ${showCountryDropdown ? 'rotate-180' : ''}`}
                                    >
                                      <path
                                        d='M12.5 15.3751L6.5 9.3751L7.9 7.9751L12.5 12.5751L17.1 7.9751L18.5 9.3751L12.5 15.3751Z'
                                        fill='#797B89'
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {showCountryDropdown && (
                                  <div className='absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-[#483cb5] bg-[#1a1b23] py-1'>
                                    <input
                                      type='text'
                                      placeholder='Search country...'
                                      value={countryQuery}
                                      onChange={(e) => setCountryQuery(e.target.value)}
                                      className='w-full border-b border-[#483cb5] bg-transparent px-4 py-2 text-sm text-white focus:outline-none'
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                    {filteredCountries.length > 0 ? (
                                      filteredCountries.map((country) => (
                                        <div
                                          key={`country-${country}`}
                                          className='w-full cursor-pointer px-4 py-2 text-left text-sm text-white hover:bg-[#2a2b33]'
                                          onClick={() => handleCountrySelect(country)}
                                        >
                                          {country}
                                        </div>
                                      ))
                                    ) : (
                                      <div className='px-4 py-2 text-sm text-white'>
                                        No country found
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className='flex w-full flex-1 items-center gap-1.5 lg:w-[200px] xl:w-[220px] xlm:w-[253px]'>
                              <div className='relative w-full'>
                                {/* Hidden input to store the actual city value for form submission */}
                                <input
                                  type='hidden'
                                  name='city'
                                  value={selectedCity}
                                  rules={cities.length === 0 ? '' : 'required'}
                                />
                                <button
                                  type='button'
                                  className={`inline-flex h-12 w-full items-center justify-between rounded-lg border border-[#483cb5] bg-transparent p-3.5 text-sm ${cities.length === 0 ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'} text-[#b3b3b3] focus:border-[#6daad3]`}
                                  onClick={() =>
                                    cities.length > 0 && setShowCityDropdown(!showCityDropdown)
                                  }
                                  disabled={!selectedCountry || cities.length === 0}
                                >
                                  <span>
                                    {selectedCity ||
                                      (cities.length === 0 && selectedCountry
                                        ? 'No cities available'
                                        : 'City')}
                                  </span>
                                  <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    width='25'
                                    height='24'
                                    viewBox='0 0 25 24'
                                    fill='none'
                                    className={`ml-2 transform transition-transform ${showCityDropdown ? 'rotate-180' : ''}`}
                                  >
                                    <path
                                      d='M12.5 15.3751L6.5 9.3751L7.9 7.9751L12.5 12.5751L17.1 7.9751L18.5 9.3751L12.5 15.3751Z'
                                      fill='#797B89'
                                    />
                                  </svg>
                                </button>
                                {showCityDropdown && cities.length > 0 && (
                                  <div className='absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-[#483cb5] bg-[#1a1b23] py-1'>
                                    <input
                                      type='text'
                                      placeholder='Search city...'
                                      value={cityQuery}
                                      onChange={(e) => setCityQuery(e.target.value)}
                                      className='w-full border-b border-[#483cb5] bg-transparent px-4 py-2 text-sm text-white focus:outline-none'
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                    {filteredCities.length === 0 ? (
                                      <div className='px-4 py-2 text-sm text-white'>
                                        No cities found
                                      </div>
                                    ) : (
                                      filteredCities.map((city, index) => (
                                        <div
                                          key={`city-${city}-${index}`}
                                          className='w-full cursor-pointer px-4 py-2 text-left text-sm text-white hover:bg-[#2a2b33]'
                                          onClick={() => handleCitySelect(city)}
                                        >
                                          {city}
                                        </div>
                                      ))
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </fieldset>
                    </div>

                    {/* Ambassador Track */}
                    <div className='col-span-full'>
                      <div className='flex flex-col gap-[24px]'>
                        <div className='flex flex-col gap-[24px]'>
                          <label className='text-base font-medium text-white'>
                            Preferred Ambassador Track<span className='text-[#d54f49]'>*</span>
                          </label>
                          <fieldset data-kw-group='ambassadorTrack' rules='required'>
                            <div className='flex flex-col gap-[20px] lg:flex-row xlm:gap-[25px]'>
                              <div className='flex items-start gap-[5px] xlm:w-[343px]'>
                                <input
                                  type='radio'
                                  name='ambassadorTrack'
                                  value='kol'
                                  className='relative mt-1 h-4 w-4 appearance-none rounded-full border-2 border-white checked:border-white checked:after:absolute checked:after:left-1/2 checked:after:top-1/2 checked:after:h-2 checked:after:w-2 checked:after:-translate-x-1/2 checked:after:-translate-y-1/2 checked:after:rounded-full checked:after:bg-[#6daad3]'
                                />
                                <div className='flex flex-wrap items-center gap-1'>
                                  <span className='text-sm text-[#e0e0e0]'>
                                    Key Opinion Leader (KOL)
                                  </span>
                                  <span className='flex items-center text-sm text-[#e0e0e0]'>
                                    Ambassador
                                    <div className='group relative'>
                                      <ExportedImage
                                        src={informationIcon}
                                        alt='informationIcon'
                                        className='ml-[5px] h-[18px] w-[18px]'
                                      />
                                      <div className='absolute bottom-full left-1/2 mb-2 hidden w-[240px] -translate-x-1/2 group-hover:block'>
                                        <div className='relative'>
                                          <ExportedImage
                                            src={infoBg}
                                            className='w-full'
                                            alt='infoBg'
                                          />
                                          <div className='absolute inset-0 my-auto flex p-4'>
                                            <p className='text-center text-xs font-medium leading-[1.4] text-white'>
                                              Must have at least 5,000 followers or subscribers
                                              (combined across all social channels)
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </span>
                                </div>
                              </div>
                              <div className='flex items-start gap-[5px] xlm:w-[343px]'>
                                <input
                                  type='radio'
                                  name='ambassadorTrack'
                                  value='regional'
                                  className='relative mt-1 h-4 w-4 appearance-none rounded-full border-2 border-white checked:border-white checked:after:absolute checked:after:left-1/2 checked:after:top-1/2 checked:after:h-2 checked:after:w-2 checked:after:-translate-x-1/2 checked:after:-translate-y-1/2 checked:after:rounded-full checked:after:bg-[#6daad3]'
                                />
                                <div className='flex flex-wrap items-center gap-1'>
                                  <span className='text-sm text-[#e0e0e0]'>
                                    Regional Ambassador
                                  </span>
                                  <div className='group relative'>
                                    <ExportedImage
                                      src={informationIcon}
                                      alt='informationIcon'
                                      className='ml-[5px] h-[18px] w-[18px]'
                                    />
                                    <div className='absolute bottom-full left-1/2 mb-2 hidden w-[220px] -translate-x-1/2 group-hover:block'>
                                      <div className='relative'>
                                        <ExportedImage
                                          src={infoBg}
                                          className='w-full'
                                          alt='infoBg'
                                        />
                                        <div className='absolute inset-0 my-auto flex p-2'>
                                          <p className='text-center text-xs font-medium leading-[1.4] text-white'>
                                            Must be able to represent Komodo at in-person crypto
                                            conferences and/or organize/host meetups.
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className='flex items-start gap-[5px] xlm:w-[343px]'>
                                <input
                                  type='radio'
                                  name='ambassadorTrack'
                                  value='community'
                                  className='relative mt-1 h-4 w-4 appearance-none rounded-full border-2 border-white checked:border-white checked:after:absolute checked:after:left-1/2 checked:after:top-1/2 checked:after:h-2 checked:after:w-2 checked:after:-translate-x-1/2 checked:after:-translate-y-1/2 checked:after:rounded-full checked:after:bg-[#6daad3]'
                                />
                                <div className='flex flex-wrap items-center gap-1'>
                                  <span className='text-sm text-[#e0e0e0]'>
                                    Community Ambassador
                                  </span>
                                  <div className='group relative'>
                                    <ExportedImage
                                      src={informationIcon}
                                      className='ml-[5px] h-[18px] w-[18px]'
                                      alt='informationIcon'
                                    />
                                    <div className='absolute bottom-full left-1/2 mb-2 hidden w-[260px] -translate-x-1/2 group-hover:block'>
                                      <div className='relative'>
                                        <ExportedImage
                                          src={infoBg}
                                          className='w-full'
                                          alt='infoBg'
                                        />
                                        <div className='absolute inset-0 my-auto flex p-2'>
                                          <p className='text-center text-xs font-medium leading-[1.4] text-white'>
                                            Must be able to initiate discussions, create/send memes,
                                            answer questions from community members and assist with
                                            Komodo Wallet growth campaigns (online/remote only).
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </fieldset>
                        </div>
                      </div>
                    </div>

                    {/* Relevant Experience */}
                    <div className='col-span-full'>
                      <fieldset>
                        <div className='flex flex-col'>
                          <label className='text-sm font-medium text-white'>
                            Relevant Experience<span className='text-[#d54f49]'>*</span>
                          </label>
                          <span className='pb-[10px] text-xs text-[#cccccc]'>
                            Describe your background in blockchain, content creation, community
                            building, or technical expertise. Include links to any relevant work
                            (e.g., blog posts, social profiles, videos). If you&apos;re applying for
                            multiple ambassador tracks, please make sure to explain how you&apos;re
                            uniquely qualified for each one.
                          </span>
                        </div>
                        <textarea
                          placeholder='Your message'
                          name='relevantExperience'
                          rules='required|min:50'
                          className='h-[101px] w-full rounded-lg border border-[#483cb5] bg-transparent p-3.5 text-sm text-white placeholder:text-[#b3b3b3] focus:border-[#6daad3] focus:outline-none'
                        />
                      </fieldset>
                    </div>

                    {/* Platforms/Communities */}
                    <div className='col-span-full'>
                      <fieldset data-kw-group='platforms' rules='required'>
                        <div className='flex flex-col gap-[24px]'>
                          <label className='text-base font-medium text-white'>
                            Platforms/Communities You Currently Engage With
                            <span className='text-[#d54f49]'>*</span>
                          </label>
                          <div className='flex flex-col gap-[25px] sm:flex-row sm:items-center lg:w-[950px] xl:w-[1079px]'>
                            {[
                              { icon: discord1, name: 'Discord' },
                              { icon: telegram1, name: 'Telegram' },
                              { icon: redditIcon, name: 'Reddit' },
                              { icon: twitterIcon, name: 'X.com' },
                            ].map((platform) => (
                              <label
                                key={platform.name}
                                name={platform.name}
                                className='flex w-[150px] cursor-pointer items-center gap-2 lg:w-[220px] xl:w-[269px]'
                              >
                                <div className='relative h-[18px] w-[18px]'>
                                  <input
                                    type='checkbox'
                                    name='platforms'
                                    value={platform.name}
                                    className='peer h-[18px] w-[18px] cursor-pointer appearance-none rounded-[3px] border-2 border-white transition-colors checked:border-[#6daad3] checked:bg-[#6daad3]'
                                  />
                                  <svg
                                    className='pointer-events-none absolute left-[2px] top-[2px] h-[12px] w-[12px] opacity-0 peer-checked:opacity-100'
                                    xmlns='http://www.w3.org/2000/svg'
                                    viewBox='0 0 24 24'
                                    fill='none'
                                    stroke='white'
                                    strokeWidth='3'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                  >
                                    <polyline points='20 6 9 17 4 12'></polyline>
                                  </svg>
                                </div>
                                <div className='flex items-center gap-1'>
                                  <ExportedImage
                                    src={platform.icon}
                                    className='h-[24px] w-[24px]'
                                    alt={`${platform.name}Icon`}
                                  />
                                  <span className='text-sm text-[#e0e0e0]'>{platform.name}</span>
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>
                      </fieldset>
                    </div>

                    {/* Portfolio Links */}
                    <div className='flex flex-col gap-[24px]'>
                      <fieldset>
                        <div className='flex flex-col'>
                          <label className='text-sm font-medium text-white'>Portfolio Links</label>
                          <span className='text-xs text-[#cccccc]'>
                            Share any links to your content, social media, or related work.
                          </span>
                        </div>
                        <input
                          type='url'
                          name='portfolioLink'
                          placeholder='Links'
                          className='h-12 w-full rounded-lg border border-[#483cb5] bg-transparent p-3.5 text-sm text-white placeholder:text-[#b3b3b3] focus:border-[#6daad3] focus:outline-none'
                        />
                      </fieldset>
                    </div>

                    {/* Availability */}
                    <div className='flex flex-col gap-[24px]'>
                      <fieldset>
                        <div className='flex flex-col'>
                          <label className='text-sm font-medium text-white'>
                            Availability<span className='text-[#d54f49]'>*</span>
                          </label>
                          <span className='text-xs text-[#cccccc]'>
                            How many hours per week can you dedicate to the program?
                          </span>
                        </div>
                        <div className='relative'>
                          <select
                            className='h-12 w-full appearance-none rounded-lg border border-[#483cb5] bg-transparent px-3.5 py-1 text-sm text-white placeholder:text-[#b3b3b3] focus:border-[#6daad3] focus:outline-none'
                            defaultValue=''
                            name='availability'
                            rules='required'
                          >
                            <option value='' disabled className='bg-[#1a1b23] text-[#b3b3b3]'>
                              Select hours per week
                            </option>
                            <option value='1-5' className='bg-[#1a1b23] text-white'>
                              1-5 hours
                            </option>
                            <option value='6-10' className='bg-[#1a1b23] text-white'>
                              6-10 hours
                            </option>
                            <option value='10-20' className='bg-[#1a1b23] text-white'>
                              10-20 hours
                            </option>
                            <option value='20+' className='bg-[#1a1b23] text-white'>
                              20+ hours
                            </option>
                          </select>
                          <div className='pointer-events-none absolute inset-y-0 right-0 flex items-center px-3'>
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width='25'
                              height='24'
                              viewBox='0 0 25 24'
                              fill='none'
                              className={`ml-2 transform transition-transform ${showCountryDropdown ? 'rotate-180' : ''}`}
                            >
                              <path
                                d='M12.5 15.3751L6.5 9.3751L7.9 7.9751L12.5 12.5751L17.1 7.9751L18.5 9.3751L12.5 15.3751Z'
                                fill='#797B89'
                              />
                            </svg>
                          </div>
                        </div>
                      </fieldset>
                    </div>

                    {/* Agreement Checkbox */}
                    <div className='col-span-full'>
                      <fieldset>
                        <label className='flex w-full flex-row items-center gap-2'>
                          <div className='relative h-[18px] w-[18px]'>
                            <input
                              type='checkbox'
                              name='termsAgreement'
                              rules='required'
                              className='absolute h-[18px] w-[18px] appearance-none rounded-[3.11px] border-2 border-white checked:bg-white checked:after:absolute checked:after:left-[5px] checked:after:top-[1px] checked:after:h-[12px] checked:after:w-[6px] checked:after:rotate-45 checked:after:border-b-2 checked:after:border-r-2 checked:after:border-[#000] checked:after:content-[""]'
                            />
                          </div>
                          <span className='ml-4 text-sm text-white md:ml-2'>
                            I agree to receive updates and communications about the Komodo
                            Ambassador Program.
                          </span>
                        </label>
                      </fieldset>
                    </div>
                    {/* Submit Button */}
                    <div className='col-span-full'>
                      <fieldset className='flex flex-col items-center justify-center gap-4 -xms:w-[250px] -xms:items-start'>
                        <ReCAPTCHA sitekey='6LcMeu0iAAAAAPSzR-vtTQkxULQXQZ5yUqA-VtwP' />
                        <motion.button
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.97 }}
                          transition={{ duration: 0.2 }}
                          type='submit'
                          className='join-now-btn w-full items-center justify-center px-8 py-3 text-center text-base font-medium text-white lg:w-[683px] -xms:max-w-[250px]'
                        >
                          Pre-Apply Now
                        </motion.button>
                      </fieldset>
                    </div>
                  </div>
                </form>
              </div>
            </motion.section>
          </div>
          <div className='hidden xl:col-span-2 xl:block'></div>
        </div>
      </main>
    </>
  );
}
